import { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getURLHistory } from '../../api/monitoring';
import { FiArrowLeft, FiClock, FiTrendingUp, FiActivity, FiShoppingCart, FiRefreshCw, FiExternalLink } from 'react-icons/fi';
import type { URLHistoryResponse, TimelineEntry, FieldHistory, StabilityMetrics } from '../../api/types';
import styles from './URLHistoryPage.module.css';

type TabType = 'timeline' | 'fields' | 'stability' | 'ecommerce';

export default function URLHistoryPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const url = searchParams.get('url') || '';
  
  const [activeTab, setActiveTab] = useState<TabType>('timeline');

  const { data, isLoading, isError, refetch } = useQuery<URLHistoryResponse>({
    queryKey: ['url-history', id, url],
    queryFn: () => getURLHistory(id!, url),
    enabled: !!id && !!url,
  });

  const handleBack = () => {
    navigate(`/monitoring/${id}`);
  };

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <FiRefreshCw className={styles.spinIcon} size={24} />
        <span>Loading URL history...</span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={styles.errorState}>
        <p>Error loading URL history. Please try again.</p>
        <button onClick={() => refetch()} className={styles.retryButton}>
          <FiRefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {/* Header */}
      <div className={styles.header}>
        <button onClick={handleBack} className={styles.backButton}>
          <FiArrowLeft size={18} />
          Back to Project
        </button>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>URL History</h1>
          <a href={url} target="_blank" rel="noopener noreferrer" className={styles.urlLink}>
            {url}
            <FiExternalLink size={14} />
          </a>
        </div>
      </div>

      {/* Quick Stats */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Days Monitored</span>
          <span className={styles.statValue}>{data.days_monitored}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Total Snapshots</span>
          <span className={styles.statValue}>{data.stability.total_snapshots}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Total Changes</span>
          <span className={styles.statValue}>{data.stability.total_changes}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Changes/Week</span>
          <span className={styles.statValue}>{data.stability.changes_per_week.toFixed(1)}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Stability</span>
          <span className={`${styles.statValue} ${data.stability.is_stable ? styles.stable : styles.unstable}`}>
            {data.stability.is_stable ? '✓ Stable' : '⚠ Unstable'}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'timeline' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('timeline')}
        >
          <FiClock size={16} />
          Timeline
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'fields' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('fields')}
        >
          <FiTrendingUp size={16} />
          Field History
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'stability' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('stability')}
        >
          <FiActivity size={16} />
          Stability
        </button>
        {data.availability_history && (
          <button
            className={`${styles.tab} ${activeTab === 'ecommerce' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('ecommerce')}
          >
            <FiShoppingCart size={16} />
            E-Commerce
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {activeTab === 'timeline' && <TimelineView timeline={data.timeline} />}
        {activeTab === 'fields' && <FieldHistoryView fieldHistory={data.field_history} />}
        {activeTab === 'stability' && <StabilityView stability={data.stability} changesByField={data.changes_by_field} />}
        {activeTab === 'ecommerce' && data.availability_history && (
          <ECommerceView availability={data.availability_history} />
        )}
      </div>
    </div>
  );
}

// Timeline View Component
function TimelineView({ timeline }: { timeline: TimelineEntry[] }) {
  if (!timeline.length) {
    return <div className={styles.emptyState}>No timeline entries yet.</div>;
  }

  return (
    <div className={styles.timeline}>
      {timeline.map((entry) => (
        <div key={entry.snapshot_id} className={styles.timelineItem}>
          <div className={styles.timelineDot} />
          <div className={styles.timelineContent}>
            <div className={styles.timelineHeader}>
              <span className={styles.timelineDate}>
                {new Date(entry.timestamp).toLocaleString()}
              </span>
              <span className={`${styles.statusBadge} ${getStatusClass(entry.status_code)}`}>
                {entry.status_code}
              </span>
            </div>
            {entry.title && <div className={styles.timelineTitle}>{entry.title}</div>}
            {entry.changes_from_previous.length > 0 && (
              <div className={styles.changesList}>
                {entry.changes_from_previous.map((change, i) => (
                  <div key={i} className={`${styles.changeItem} ${styles[change.severity]}`}>
                    <span className={styles.changeField}>{change.field_name}</span>
                    <span className={styles.changeType}>{change.change_type}</span>
                    {change.old_value && (
                      <span className={styles.oldValue}>"{change.old_value}"</span>
                    )}
                    <span className={styles.arrow}>→</span>
                    {change.new_value && (
                      <span className={styles.newValue}>"{change.new_value}"</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Field History View Component
function FieldHistoryView({ fieldHistory }: { fieldHistory: Record<string, FieldHistory> }) {
  const fields = Object.entries(fieldHistory);

  if (!fields.length) {
    return <div className={styles.emptyState}>No field history available.</div>;
  }

  return (
    <div className={styles.fieldHistoryGrid}>
      {fields.map(([fieldName, history]) => (
        <div key={fieldName} className={styles.fieldCard}>
          <div className={styles.fieldHeader}>
            <span className={styles.fieldName}>{fieldName}</span>
            <span className={styles.changeCount}>{history.total_changes} changes</span>
          </div>
          <div className={styles.fieldValues}>
            {history.values.slice(0, 5).map((value, i) => (
              <div key={i} className={styles.fieldValue}>
                <span className={styles.valueDate}>
                  {new Date(value.timestamp).toLocaleDateString()}
                </span>
                <span className={styles.valueText}>{value.value || '(empty)'}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Stability View Component
function StabilityView({
  stability,
  changesByField
}: {
  stability: StabilityMetrics;
  changesByField: Record<string, number>;
}) {
  const sortedFields = Object.entries(changesByField).sort((a, b) => b[1] - a[1]);

  return (
    <div className={styles.stabilityView}>
      <div className={styles.stabilityMetrics}>
        <div className={styles.metricCard}>
          <span className={styles.metricLabel}>Most Changed Field</span>
          <span className={styles.metricValue}>{stability.most_changed_field || 'N/A'}</span>
          {stability.most_changed_count > 0 && (
            <span className={styles.metricSubtext}>{stability.most_changed_count} changes</span>
          )}
        </div>
        <div className={styles.metricCard}>
          <span className={styles.metricLabel}>Last Change</span>
          <span className={styles.metricValue}>
            {stability.last_change_date
              ? new Date(stability.last_change_date).toLocaleDateString()
              : 'Never'}
          </span>
        </div>
        <div className={styles.metricCard}>
          <span className={styles.metricLabel}>Stable Fields</span>
          <span className={styles.metricValue}>{stability.fields_never_changed.length}</span>
        </div>
      </div>

      <div className={styles.changeBreakdown}>
        <h3 className={styles.sectionTitle}>Changes by Field</h3>
        <div className={styles.barChart}>
          {sortedFields.map(([field, count]) => (
            <div key={field} className={styles.barRow}>
              <span className={styles.barLabel}>{field}</span>
              <div className={styles.barContainer}>
                <div
                  className={styles.bar}
                  style={{ width: `${(count / Math.max(...Object.values(changesByField))) * 100}%` }}
                />
              </div>
              <span className={styles.barValue}>{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// E-Commerce View Component
function ECommerceView({ availability }: { availability: any }) {
  return (
    <div className={styles.ecommerceView}>
      <div className={styles.currentStatus}>
        <div className={styles.statusCard}>
          <span className={styles.statusLabel}>Current Status</span>
          <span className={`${styles.statusValue} ${availability.current_status === 'in_stock' ? styles.inStock : styles.outOfStock}`}>
            {availability.current_status || 'Unknown'}
          </span>
        </div>
        <div className={styles.statusCard}>
          <span className={styles.statusLabel}>Current Price</span>
          <span className={styles.priceValue}>{availability.current_price || 'N/A'}</span>
        </div>
      </div>

      {availability.price_changes?.length > 0 && (
        <div className={styles.priceHistory}>
          <h3 className={styles.sectionTitle}>Price History</h3>
          <div className={styles.priceList}>
            {availability.price_changes.map((change: any, i: number) => (
              <div key={i} className={styles.priceChange}>
                <span className={styles.priceDate}>
                  {new Date(change.timestamp).toLocaleDateString()}
                </span>
                <span className={styles.priceFrom}>{change.from_price || 'N/A'}</span>
                <span className={styles.arrow}>→</span>
                <span className={styles.priceTo}>{change.to_price}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {availability.out_of_stock_periods?.length > 0 && (
        <div className={styles.stockHistory}>
          <h3 className={styles.sectionTitle}>Out of Stock Periods</h3>
          <div className={styles.stockList}>
            {availability.out_of_stock_periods.map((period: any, i: number) => (
              <div key={i} className={styles.stockPeriod}>
                <span className={styles.periodDates}>
                  {new Date(period.start).toLocaleDateString()} -
                  {period.end ? new Date(period.end).toLocaleDateString() : 'Ongoing'}
                </span>
                <span className={styles.periodDuration}>{period.duration_hours}h</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function for status color
function getStatusClass(status: number): string {
  if (status >= 200 && status < 300) return styles.success;
  if (status >= 300 && status < 400) return styles.warning;
  if (status >= 400) return styles.error;
  return styles.neutral;
}

